import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCircleExclamation,
    faImage,
    faTrashCan,
    faUpload,
} from "@fortawesome/pro-solid-svg-icons";
import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "frontend-components";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTaskImages, useUploadTaskImage } from "@dashboard/hooks/useTaskImageQuery";
import { faPlus } from "@fortawesome/sharp-regular-svg-icons";

const MAX_IMAGE_SIZE_BYTES = 5_000_000;
const MAX_IMAGES_PER_TASK = 20;

interface PendingFile {
    file: File;
    objectUrl: string;
    status: "idle" | "error";
}

interface TaskImageModalProps {
    taskId: number;
}

export function TaskImageModal({ taskId }: TaskImageModalProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [pending, setPending] = useState<PendingFile[]>([]);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const uploadMutation = useUploadTaskImage();
    const { data: existingImages } = useTaskImages(taskId);
    const existingCount = existingImages?.total ?? 0;

    // Revoke any remaining object URLs if the component unmounts while
    // files are pending. Uses a ref to read the latest pending list without
    // re-running the effect on every change.
    const pendingRef = useRef(pending);
    pendingRef.current = pending;
    useEffect(
        () => () => {
            pendingRef.current.forEach(({ objectUrl }) => URL.revokeObjectURL(objectUrl));
        },
        [],
    );

    const handleFilesSelected = (files: FileList | null) => {
        if (!files) return;

        const errors: string[] = [];
        const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));

        const oversized = imageFiles.filter((f) => f.size > MAX_IMAGE_SIZE_BYTES);
        if (oversized.length > 0) {
            errors.push(
                t("list.fileTooLarge", {
                    count: oversized.length,
                    maxMb: MAX_IMAGE_SIZE_BYTES / 1_000_000,
                }),
            );
        }

        const validFiles = imageFiles.filter((f) => f.size <= MAX_IMAGE_SIZE_BYTES);
        const remainingSlots = Math.max(0, MAX_IMAGES_PER_TASK - existingCount - pending.length);
        const accepted = validFiles.slice(0, remainingSlots);

        if (validFiles.length > remainingSlots) {
            errors.push(t("list.tooManyImages", { max: MAX_IMAGES_PER_TASK }));
        }

        setValidationErrors(errors);

        if (accepted.length > 0) {
            const newFiles: PendingFile[] = accepted.map((file) => ({
                file,
                objectUrl: URL.createObjectURL(file),
                status: "idle",
            }));
            setPending((prev) => [...prev, ...newFiles]);
        }

        if (inputRef.current) inputRef.current.value = "";
    };

    const removePending = (index: number) => {
        setPending((prev) => {
            URL.revokeObjectURL(prev[index].objectUrl);
            return prev.filter((_, i) => i !== index);
        });
    };

    const handleUpload = async () => {
        const results = await Promise.allSettled(
            pending.map(({ file }) => uploadMutation.mutateAsync({ taskId, file })),
        );

        const failed = pending.filter((_, i) => results[i].status === "rejected");
        const succeeded = pending.filter((_, i) => results[i].status === "fulfilled");

        succeeded.forEach(({ objectUrl }) => URL.revokeObjectURL(objectUrl));

        if (failed.length === 0) {
            setPending([]);
            setOpen(false);
        } else {
            setPending(failed.map((f) => ({ ...f, status: "error" })));
        }
    };

    const handleOpenChange = (next: boolean) => {
        if (!next) {
            pending.forEach(({ objectUrl }) => URL.revokeObjectURL(objectUrl));
            setPending([]);
            setValidationErrors([]);
        }
        setOpen(next);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger
                render={
                    <button
                        type="button"
                        className="text-xs text-compendia-foreground inline-flex items-center cursor-pointer"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-1 text-[10px]" />
                        <span>{t("list.add_image")}</span>
                    </button>
                }
            />
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t("list.addImageTitle")}</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handleFilesSelected(e.target.files)}
                    />

                    <Button
                        variant="outline"
                        onClick={() => inputRef.current?.click()}
                        className="w-full"
                        type="button"
                    >
                        <FontAwesomeIcon icon={faUpload} className="mr-2" />
                        {t("list.selectImages")}
                    </Button>

                    {validationErrors.length > 0 && (
                        <ul className="flex flex-col gap-1">
                            {validationErrors.map((msg, i) => (
                                <li
                                    key={i}
                                    className="text-xs text-chameleon-pink flex items-start gap-1"
                                >
                                    <FontAwesomeIcon
                                        icon={faCircleExclamation}
                                        className="mt-0.5 shrink-0"
                                    />
                                    {msg}
                                </li>
                            ))}
                        </ul>
                    )}

                    {pending.length > 0 && (
                        <div className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                            {pending.map(({ objectUrl, file, status }, idx) => (
                                <div key={idx} className="relative group">
                                    <img
                                        src={objectUrl}
                                        alt={file.name}
                                        className={`w-full aspect-square object-cover rounded-[3px] border ${
                                            status === "error"
                                                ? "border-chameleon-pink"
                                                : "border-compendia-border"
                                        }`}
                                    />
                                    {status === "error" && (
                                        <div className="absolute inset-0 bg-chameleon-pink/20 rounded-[3px] flex items-center justify-center pointer-events-none">
                                            <FontAwesomeIcon
                                                icon={faCircleExclamation}
                                                className="text-chameleon-pink"
                                            />
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="absolute top-1 right-1 w-6 h-6 bg-chameleon-pink/80 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                        onClick={() => removePending(idx)}
                                        aria-label={t("common.remove")}
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} size="xs" />
                                    </button>
                                    <p className="text-xs text-compendia-muted truncate mt-1">
                                        {file.name}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    {pending.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-8 border border-dashed border-compendia-border rounded-[3px] text-compendia-muted">
                            <FontAwesomeIcon icon={faImage} size="2x" className="mb-2" />
                            <p className="text-sm">{t("list.noImagesSelected")}</p>
                        </div>
                    )}

                    <div className="flex justify-end gap-2">
                        <DialogClose
                            render={
                                <Button type="button" variant="outline">
                                    {t("common.cancel")}
                                </Button>
                            }
                        />
                        <Button
                            type="button"
                            variant="submit"
                            size="submit"
                            onClick={handleUpload}
                            disabled={pending.length === 0 || uploadMutation.isPending}
                        >
                            {uploadMutation.isPending ? t("common.saving") : t("common.save")}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
